import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { OrganizationKind } from '@/enums/organization'
import { useAppStore } from '@/stores/app'

/**
 * Layout state shared by `Default.vue` and its components (vue-spec `structure.md` —
 * `layouts/layout.ts`). Module-level, because there is one chrome per window: two copies of
 * "is the sidebar open" is how a menu closes itself on one component and not the other.
 *
 * Dark mode is deliberately NOT here, and it is not the topbar's either. Nuxt UI auto-registers
 * VueUse's `useDark`, and `stores/app.ts` wraps that SAME state as `colorMode` — one source of
 * truth, per vue-spec `theme.md`. The topbar only renders `ColorModeToggle`; duplicating the
 * state here would be the second source of truth theme.md forbids.
 */
/**
 * THE ADMINISTRATION MENU, in one place. `Sidebar.vue` renders it and `Topbar.vue` reads the
 * CURRENT entry's label for the page title the Nuxt UI dashboard navbar shows at the left —
 * two components, one list, so a section can never be named one thing in the menu and another
 * in the title bar. It lists only routes that EXIST.
 *
 * `routes` is every route of a section, so a DETAIL page (a user, a client) keeps its section
 * highlighted in the menu and titled in the navbar. The labels are i18n KEYS, not prose: the
 * strings stay in `libs/i18n/locales/*`.
 */
export interface NavItem {
    /** The route this entry navigates to. */
    name:     string
    /** The i18n key of its label (`nav.*`). */
    labelKey: string
    icon:     string
    /** Every route of this section. */
    routes:   string[]
    /*
     * FLEET: an entry a reader cannot use is left OUT rather than greyed out — a greyed-out
     * entry promises something behind it, and what is behind it is a 403.
     */
    adminOnly?: boolean
    /*
     * FLEET: which kinds of organisation the entry is for (OrganizationKind); absent is
     * every kind. A workshop's people see the map and the vehicles their service cases
     * assign them and nothing of the fleet's business; the platform's own screens are
     * the operator's. Left OUT for anybody else, exactly like `adminOnly`.
     */
    kinds?:     OrganizationKind[]
    /*
     * FLEET: how many of something is waiting behind this entry — the name of the count
     * `Sidebar.vue` reads from its store. A property of a nav item and not a special case
     * for one screen: the next thing worth counting gets a name here and a line there.
     */
    badge?:     NavBadgeSource
}

/** FLEET: the counts a menu entry can carry (`stores/alerts.ts`, `stores/reminders.ts`). */
export type NavBadgeSource = 'alerts' | 'reminders'

/**
 * A SECTION of the menu. `labelKey` is the small uppercase header above the group, or `null`
 * for the opening group — the landing page needs no heading, and one on its own would only
 * push the first row away from the brand.
 */
export interface NavGroup {
    labelKey: string | null
    items:    NavItem[]
}

/**
 * THE MENU, IN GROUPS. Rows in one undifferentiated column say nothing about what the app is
 * FOR; grouped under terse headers they say it in a word or two. List only routes that EXIST.
 *
 * FLEET: one entry per screen, in the order somebody opening the console asks their
 * questions: where are the vehicles, and only then how the console is set up. A new screen is
 * one line here and one route.
 */
/** FLEET: the kinds that read a fleet's business — everybody but a workshop. */
export const FLEET_KINDS = [OrganizationKind.Operator, OrganizationKind.Customer]

export const NAV_GROUPS: NavGroup[] = [
    {
        labelKey: null,
        items: [
            { name: 'map',       labelKey: 'nav.map',       icon: 'i-lucide-map',            routes: ['map'] },
            { name: 'devices',   labelKey: 'nav.vehicles',  icon: 'i-lucide-car-front',      routes: ['devices', 'device'] },
            { name: 'drivers',   labelKey: 'nav.drivers',   icon: 'i-lucide-id-card',        routes: ['drivers'],   kinds: FLEET_KINDS },
            { name: 'costs',     labelKey: 'nav.costs',     icon: 'i-lucide-receipt-euro',   routes: ['costs'],     kinds: FLEET_KINDS },
            { name: 'trips',     labelKey: 'nav.trips',     icon: 'i-lucide-route',          routes: ['trips'],     kinds: FLEET_KINDS },
            { name: 'stats',     labelKey: 'nav.stats',     icon: 'i-lucide-chart-column',   routes: ['stats'],     kinds: FLEET_KINDS },
            { name: 'alerts',    labelKey: 'nav.alerts',    icon: 'i-lucide-bell',           routes: ['alerts'],    kinds: FLEET_KINDS, badge: 'alerts' },
            { name: 'reminders', labelKey: 'nav.reminders', icon: 'i-lucide-calendar-clock', routes: ['reminders'], kinds: FLEET_KINDS, badge: 'reminders' },
            { name: 'zones',     labelKey: 'nav.zones',     icon: 'i-lucide-map-pinned',     routes: ['zones'],     kinds: FLEET_KINDS },
        ],
    },
    {
        /*
         * The service loop (docs/service-cases.md): the cases and the parts ordered against
         * them. One route per screen, named for whoever reads it — a workshop's list is the
         * cases ASSIGNED to it, which is the same route under another word, so it is a second
         * entry for that kind rather than a label that changes. The dashboard is Stanić's.
         */
        labelKey: 'nav.service',
        items: [
            { name: 'out-of-service', labelKey: 'nav.out_of_service', icon: 'i-lucide-gauge',          routes: ['out-of-service'],               kinds: [OrganizationKind.Operator] },
            { name: 'service-cases',  labelKey: 'nav.service_cases',  icon: 'i-lucide-clipboard-list', routes: ['service-cases', 'service-case'], kinds: FLEET_KINDS },
            { name: 'service-cases',  labelKey: 'nav.assigned_cases', icon: 'i-lucide-clipboard-list', routes: ['service-cases', 'service-case'], kinds: [OrganizationKind.Workshop] },
            { name: 'part-orders',    labelKey: 'nav.part_orders',    icon: 'i-lucide-package',        routes: ['part-orders'],                  kinds: [OrganizationKind.Operator, OrganizationKind.Workshop] },
        ],
    },
    {
        labelKey: 'nav.configuration',
        items: [
            // The operator, the customers and the workshops: the operator's administrators'.
            { name: 'organizations', labelKey: 'nav.organizations', icon: 'i-lucide-building-2', routes: ['organizations'], adminOnly: true, kinds: [OrganizationKind.Operator] },
            // A schedule lists the recipients, which are people's addresses, so every route
            // under /reports is an administrator's.
            { name: 'reports',       labelKey: 'nav.reports',       icon: 'i-lucide-mail-check', routes: ['reports'], adminOnly: true, kinds: FLEET_KINDS },
            // The operator's people install the trackers, any role.
            { name: 'install-guide', labelKey: 'nav.install_guide', icon: 'i-lucide-book-open',  routes: ['install-guide'], kinds: [OrganizationKind.Operator] },
            // Every account: a reader keeps their own passkeys and password there.
            { name: 'settings',      labelKey: 'nav.settings',      icon: 'i-lucide-settings',   routes: ['settings'] },
        ],
    },
]

/** FLEET: whether an entry is for this person — their role and their organisation's kind. */
export const navItemAllowed = (item: NavItem, admin: boolean, kind: string): boolean =>
    (!item.adminOnly || admin) && (!item.kinds || item.kinds.includes(kind as OrganizationKind))

/** Every entry, flattened — what the topbar titles the page from. The groups are the model. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

/*
 * The i18n key of the page title for a route, falling back to the product name. The kind
 * picks between two entries of one route ("Servisni nalozi" / "Dodijeljeni nalozi").
 */
export const navTitleKey = (routeName: string, kind = ''): string =>
    NAV_ITEMS.find((item) => item.routes.includes(routeName) && (!kind || !item.kinds || item.kinds.includes(kind as OrganizationKind)))?.labelKey
    ?? NAV_ITEMS.find((item) => item.routes.includes(routeName))?.labelKey
    ?? 'brand.product_name'

/**
 * IS THE MOBILE DRAWER OPEN. Module-level, and the only copy — below 1024px the sidebar is not
 * a column any more but an overlay over the whole shell, and an overlay that two components
 * disagree about is an overlay that cannot be closed.
 *
 * It only ever MEANS anything on mobile. On the desktop the sidebar is rendered because it is
 * the layout's left column, not because this is `true`, which is why `useLayoutShell()` forces
 * it back to `false` the moment the window grows past the breakpoint: a flag left set by a
 * phone-width session would otherwise re-open the drawer the next time the window narrows.
 */
const menuOpen = ref(false)

/**
 * FLEET: IS THE DESKTOP SIDEBAR FOLDED DOWN TO ITS ICONS. Not a way of closing it — the column
 * is still there, with every entry, its tooltip and its badge dot — but this console's home is
 * a map, and a map wants the width (a recorded deviation, CLAUDE.md). Remembered per browser,
 * unlike the drawer: a drawer left open is nobody's preference, a folded menu is. It means
 * nothing on mobile, where the drawer is always the full `w-64`.
 */
const collapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')

const toggleCollapsed = (): void => {
    collapsed.value = !collapsed.value
    localStorage.setItem('sidebarCollapsed', String(collapsed.value))
}

const openMenu = (): void => {
    menuOpen.value = true
}

const closeMenu = (): void => {
    menuOpen.value = false
}

const toggleMenu = (): void => {
    menuOpen.value = !menuOpen.value
}

/**
 * The state and the ONE action per verb. Every component that can open or close the drawer —
 * the topbar's hamburger, the drawer's own X, a nav row, the backdrop — calls one of these
 * three; none of them writes `menuOpen` itself.
 */
export const useLayout = () => {
    const appStore = useAppStore()

    const isMobile = computed((): boolean => appStore.isMobile())

    return { menuOpen, isMobile, openMenu, closeMenu, toggleMenu, collapsed, toggleCollapsed }
}

/**
 * THE DRAWER'S BEHAVIOUR, wired once by `Default.vue` and nowhere else.
 *
 * A drawer that can be opened and not closed is what the shell shipped with, so the four ways
 * out live together here rather than one per component: Escape, a route change, growing past
 * the breakpoint, and — in the layout, because it is a paint and not a behaviour — the
 * backdrop. While it is open the body cannot scroll, or the page behind the overlay scrolls
 * under the thumb instead of the menu.
 *
 * The listener is on `document` for the whole life of the shell rather than added and removed
 * around `menuOpen`: one registration, no window where a key lands between the watcher and the
 * listener. It does nothing at all unless the drawer is open.
 */
export const useLayoutShell = () => {
    const { isMobile } = useLayout()
    const route        = useRoute()

    /** What `body` had before the drawer took it — restored verbatim, not blanked. */
    let bodyOverflow: string | null = null

    const lockBody = (): void => {
        if (bodyOverflow === null) {
            bodyOverflow = document.body.style.overflow
        }

        document.body.style.overflow = 'hidden'
    }

    const unlockBody = (): void => {
        if (bodyOverflow === null) {
            return
        }

        document.body.style.overflow = bodyOverflow
        bodyOverflow = null
    }

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' && menuOpen.value) {
            closeMenu()
        }
    }

    // Growing past the breakpoint: the sidebar is the layout's own column again, so the flag
    // that made it an overlay has to go with it.
    watch(isMobile, (mobile): void => {
        if (!mobile) {
            closeMenu()
        }
    })

    // Navigating is the most common way out of the menu — the row that was tapped has already
    // done its job. `Sidebar.vue` closes on select too; this catches every other route change
    // (a redirect, the back button, a link inside the page).
    watch(() => route.path, (): void => {
        closeMenu()
    })

    watch([menuOpen, isMobile], ([open, mobile]): void => {
        if (open && mobile) {
            lockBody()
        } else {
            unlockBody()
        }
    })

    onMounted((): void => {
        document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount((): void => {
        document.removeEventListener('keydown', onKeydown)
        unlockBody()
    })
}
